"use client";

import { useEffect, useState } from 'react';

interface GlobalThemeProviderProps {
  children: React.ReactNode;
}

export function GlobalThemeProvider({ children }: GlobalThemeProviderProps) {
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>('dark');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Load theme preference from localStorage on mount
    const savedTheme = localStorage.getItem('abbi_theme') as 'light' | 'dark' | 'system' || 'dark';
    setTheme(savedTheme);
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    // Update data-theme attribute when theme changes
    const actualTheme = theme === 'system' ? 
      (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') : 
      theme;
    
    // Apply theme to body with suppressHydrationWarning to prevent hydration mismatch
    document.body.setAttribute('data-theme', actualTheme);
    
    // Also apply to any existing data-theme elements
    const themeElements = document.querySelectorAll('[data-theme]');
    themeElements.forEach(element => {
      element.setAttribute('data-theme', actualTheme);
    });
  }, [theme, mounted]);

  // Expose theme setter globally
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).setGlobalTheme = (newTheme: 'light' | 'dark' | 'system') => {
        setTheme(newTheme);
        localStorage.setItem('abbi_theme', newTheme);
      };
    }
  }, []);

  // Prevent hydration mismatch by not rendering until mounted
  if (!mounted) {
    return <>{children}</>;
  }

  return <>{children}</>;
}
